import Tippy from '@tippyjs/react';
import { useEffect, useMemo, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import Typography from 'app/components/Typography';

import { ReactComponent as IconFilter } from 'app/assets/icons/icon-filter.svg';

import clsx from 'clsx';
import type { Instance, Props as TippyProps } from 'tippy.js';

const GlobalPopoverStyle = createGlobalStyle`
  /* Styling must work even when popover is portaled to body */
  .tippy-box[data-theme~='visual-archive-filter-popper'] {
    box-shadow: 23.4866px 32.3787px 94px rgba(0, 0, 0, 0.1),
      4.69732px 6.47574px 15.275px rgba(0, 0, 0, 0.05);
    border-radius: 6px;
    max-width: unset !important;
    background: var(--white);
    z-index: 9999;
  }

  .tippy-box[data-theme~='visual-archive-filter-popper'] .tippy-content {
    padding: 0;
  }
`;

const Main = styled.div<{ isFilter?: boolean }>`
  ${({ isFilter }) =>
    isFilter
      ? `.button-filter {
      background: var(--primary-3);
      svg path[stroke] {
        stroke: var(--primary);
      }
    }`
      : ``}
`;

const ButtonFilter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 101px;
  height: 43px;
  background: var(--neutral-2);
  border-radius: 8px;
  margin-right: 22px;
  cursor: pointer;
`;

function findNearestScrollParent(element: HTMLElement | null): HTMLElement | null {
  let current: HTMLElement | null = element;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const isScrollable =
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowX === 'auto' ||
        overflowX === 'scroll') &&
      (current.scrollHeight > current.clientHeight || current.scrollWidth > current.clientWidth);
    if (isScrollable) return current;
    current = current.parentElement;
  }
  return null;
}

export interface VisualArchiveFilterPopoverProps {
  title?: string;
  content: React.ReactNode;
  visible: boolean;
  onClickFilter: (type: 'on' | 'off') => void;
  isFilter: boolean;
  extraClassBtnFilter?: string;
  placement?: TippyProps['placement'];
}

const VisualArchiveFilterPopover: React.FC<VisualArchiveFilterPopoverProps> = ({
  title = 'Filter',
  content,
  visible,
  isFilter,
  extraClassBtnFilter = '',
  onClickFilter,
  placement = 'bottom-end',
}) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tippyInstanceRef = useRef<Instance | null>(null);

  const popperOptions = useMemo(() => {
    return {
      strategy: 'fixed' as const,
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            altAxis: true,
            tether: false,
            boundary: 'viewport',
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom', 'top', 'right', 'left'],
          },
        },
        {
          name: 'computeStyles',
          options: {
            adaptive: false,
            gpuAcceleration: false,
          },
        },
      ],
    };
  }, []);

  useEffect(() => {
    if (!visible) return;

    const scrollParent = findNearestScrollParent(triggerRef.current);
    const update = () => tippyInstanceRef.current?.popperInstance?.update();

    // Update on window scroll (page) + nearest scroll container (table/list)
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    if (scrollParent) {
      scrollParent.addEventListener('scroll', update, { passive: true });
    }

    // Initial update after render
    const raf = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      if (scrollParent) {
        scrollParent.removeEventListener('scroll', update as any);
      }
    };
  }, [visible]);

  return (
    <Main isFilter={isFilter}>
      <GlobalPopoverStyle />
      <Tippy
        interactive
        arrow={false}
        placement={placement}
        offset={[0, 8]}
        trigger="click"
        allowHTML
        visible={visible}
        content={content}
        appendTo={() => document.body}
        popperOptions={popperOptions}
        theme="visual-archive-filter-popper"
        onClickOutside={() => onClickFilter('off')}
        onMount={(instance) => {
          tippyInstanceRef.current = instance;
          instance.popperInstance?.update();
        }}
        onHidden={() => {
          tippyInstanceRef.current = null;
        }}
      >
        <ButtonFilter
          ref={triggerRef}
          className={clsx('button-filter', extraClassBtnFilter)}
          onClick={() => onClickFilter('on')}
        >
          <IconFilter />
          <Typography
            variant="title-4"
            color={isFilter ? 'primary' : 'neutral-7'}
            style={{ marginLeft: 10 }}
          >
            {title}
          </Typography>
        </ButtonFilter>
      </Tippy>
    </Main>
  );
};

export default VisualArchiveFilterPopover;
